// Flatten a parsed value into leaf paths → primitive value. This is what turns
// a nested JSON/YAML document into the canonical record's fields, so key order
// is irrelevant and every leaf gets a stable dotted/bracketed path.
import { appendPropertyPath } from './path.js';

export function typeOf(v) {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  if (v instanceof Date) return 'date';
  return typeof v; // 'string' | 'number' | 'boolean' | 'object'
}

/** @returns {Record<string, any>} path → leaf value */
export function flatten(value) {
  // Object.create(null): a leaf literally named `__proto__` (from JSON.parse or a
  // YAML key) must become an OWN property, not mutate the dict's prototype — this
  // avoids both prototype pollution and silently dropping that field.
  const out = Object.create(null);
  walk(value, '', out, new WeakSet());
  return out;
}

// `seen` tracks the ancestors on the current path so cyclic YAML alias graphs
// (&a { self: *a }) terminate instead of overflowing the stack; we remove a node
// on the way back up so legitimate diamonds (shared, non-cyclic refs) still flatten.
function walk(value, path, out, seen) {
  const t = typeOf(value);
  if (t === 'date') {
    // js-yaml resolves `2020-01-01` to a Date. `typeof` calls it an object and
    // Object.keys() on it is empty, so walking it as an object would flatten
    // EVERY date to the same `{}` leaf and report two different dates as equal
    // (REL-058). Normalize to an ISO string so dates diff by value.
    out[path || '$'] = Number.isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
    return;
  }
  if (t === 'object' || t === 'array') {
    if (seen.has(value)) {
      out[path || '$'] = '[Circular]';
      return;
    }
    seen.add(value);
  }
  if (t === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      out[path || '$'] = {}; // preserve empty object as a leaf
    } else {
      for (const k of keys) walk(value[k], appendPropertyPath(path, k), out, seen);
    }
    seen.delete(value);
  } else if (t === 'array') {
    if (value.length === 0) {
      out[path || '$'] = [];
    } else {
      value.forEach((el, i) => walk(el, `${path}[${i}]`, out, seen));
    }
    seen.delete(value);
  } else {
    out[path || '$'] = value;
  }
}

/** Equality for leaf values (deep for the empty-container leaves). */
export function valueEqual(a, b, t = typeOf(a)) {
  if (t === 'object' || t === 'array') return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
}
